import { Grid, Skeleton } from '@mui/material';
import { Card, CardBody, CardTitle } from '@apigrowfinance/pbolt-card';
import DateFormatter from '@apigrowfinance/pbolt-date-formatter';
import TextDisplay from '@apigrowfinance/pbolt-text-display';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage } from 'react-intl';

import { AccountDetails } from '@clients/accountsClient';
import { REGULAR_IN_REM } from '@pages/accountDetail/tabs/consts';

interface AccountProps {
    accountDetails: AccountDetails;
    isLoading?: boolean;
}

const Account = ({ accountDetails, isLoading }: AccountProps) => {
    const { theme } = useTheme();
    return (
        <Card isTopRadiusBorder={false}>
            {/*min-height header for chips*/}
            <CardBody
                minHeight={REGULAR_IN_REM}
                sx={{ justifyContent: 'end' }}
            />
            <CardTitle>
                {isLoading ? (
                    <Skeleton
                        width={225}
                        data-testid="account-details-loader"
                    />
                ) : (
                    <FormattedMessage
                        id="AccountDetails.details"
                        defaultMessage="Account details"
                    />
                )}
            </CardTitle>
            <CardBody>
                <Grid container sx={{ rowGap: theme.spacing(2) }}>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.accountID"
                                    defaultMessage="Account ID"
                                />
                            }
                            testId="account-id"
                            value={accountDetails?.account_id}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.customerID"
                                    defaultMessage="Customer ID"
                                />
                            }
                            testId="customer-id"
                            value={accountDetails?.customer_id}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.documentNumber"
                                    defaultMessage="Document Number"
                                />
                            }
                            testId="document-no"
                            value={accountDetails?.document_number}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.acquisitionID"
                                    defaultMessage="Acquisition ID"
                                />
                            }
                            testId="acquisition-id"
                            value={accountDetails?.acquisition_id}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.programName"
                                    defaultMessage="Program Name"
                                />
                            }
                            testId="program-name"
                            value={accountDetails?.program_name}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.programType"
                                    defaultMessage="Program Type"
                                />
                            }
                            testId="program-type"
                            value={accountDetails?.program_type}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.collectionStatus"
                                    defaultMessage="Collection Status"
                                />
                            }
                            testId="collection-status"
                            value={accountDetails?.collections_status}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.exchangeMode"
                                    defaultMessage="Exchange Mode"
                                />
                            }
                            testId="exchange-mode"
                            value={accountDetails?.exchange_mode}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.creationDate"
                                    defaultMessage="Creation Date"
                                />
                            }
                            value={
                                <DateFormatter
                                    date={accountDetails?.creation_date}
                                />
                            }
                            testId="creation-date"
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextDisplay
                            label={
                                <FormattedMessage
                                    id="AccountDetails.externalID"
                                    defaultMessage="External ID"
                                />
                            }
                            testId="external-id"
                            value={accountDetails?.external_id}
                            isLoading={isLoading}
                        />
                    </Grid>
                </Grid>
            </CardBody>
        </Card>
    );
};

export default Account;
